import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  GraduationCap, 
  User, 
  EnvelopeSimple, 
  Lock, 
  Eye, 
  EyeSlash,
  SignIn,
  UserPlus,
  Warning
} from '@phosphor-icons/react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies
axios.defaults.withCredentials = true;

export function AuthScreen({ onAuthSuccess }) {
  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const endpoint = mode === 'login' ? '/auth/login' : '/auth/register';
      const payload = mode === 'login' 
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload);
      onAuthSuccess(response.data);
    } catch (err) {
      console.error('Auth error:', err);
      setError(err.response?.data?.detail || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: 'var(--background)' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--primary)' }}>
            <GraduationCap size={40} weight="fill" color="white" />
          </div>
          <h1 className="font-display text-3xl font-black" style={{ color: 'var(--text-primary)' }}>
            FMAA Apprentice Mode
          </h1>
          <p className="font-mono text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
            Master Financial & Managerial Accounting
          </p>
        </div>

        {/* Auth Card */}
        <div className="card p-8">
          {/* Mode Toggle */}
          <div className="flex mb-6 rounded overflow-hidden" style={{ border: '1px solid var(--border)' }}>
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-3 font-mono text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                mode === 'login' 
                  ? 'bg-[#163300] text-white' 
                  : 'bg-transparent hover:bg-[#F0EBE1]'
              }`}
              data-testid="login-tab"
            >
              <SignIn size={18} />
              Sign In
            </button>
            <button
              onClick={() => setMode('register')}
              className={`flex-1 py-3 font-mono text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                mode === 'register' 
                  ? 'bg-[#163300] text-white' 
                  : 'bg-transparent hover:bg-[#F0EBE1]'
              }`}
              data-testid="register-tab"
            >
              <UserPlus size={18} />
              Register
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mb-4 p-3 rounded flex items-center gap-2 text-sm"
              style={{ backgroundColor: '#8B0000/10', color: '#8B0000', border: '1px solid #8B0000' }}
            >
              <Warning size={18} />
              {error}
            </motion.div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="block font-mono text-xs uppercase tracking-widest mb-2 opacity-60">
                  Your Name
                </label>
                <div className="relative">
                  <User 
                    size={18} 
                    className="absolute left-3 top-1/2 -translate-y-1/2 opacity-40" 
                  />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    required={mode === 'register'}
                    autoComplete="name"
                    className="w-full pl-10 pr-4 py-3 rounded border font-mono focus:outline-none focus:ring-2 focus:ring-[#163300]"
                    style={{ borderColor: 'var(--border)' }}
                    data-testid="name-input"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block font-mono text-xs uppercase tracking-widest mb-2 opacity-60">
                Email Address
              </label>
              <div className="relative">
                <EnvelopeSimple 
                  size={18} 
                  className="absolute left-3 top-1/2 -translate-y-1/2 opacity-40" 
                />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  className="w-full pl-10 pr-4 py-3 rounded border font-mono focus:outline-none focus:ring-2 focus:ring-[#163300]"
                  style={{ borderColor: 'var(--border)' }}
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block font-mono text-xs uppercase tracking-widest mb-2 opacity-60">
                Password
              </label>
              <div className="relative">
                <Lock 
                  size={18} 
                  className="absolute left-3 top-1/2 -translate-y-1/2 opacity-40" 
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  className="w-full pl-10 pr-12 py-3 rounded border font-mono focus:outline-none focus:ring-2 focus:ring-[#163300]"
                  style={{ borderColor: 'var(--border)' }}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 opacity-40 hover:opacity-100"
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-4 flex items-center justify-center gap-2"
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                  />
                  {mode === 'login' ? 'Signing in...' : 'Creating account...'}
                </>
              ) : (
                <>
                  {mode === 'login' ? <SignIn size={20} /> : <UserPlus size={20} />}
                  {mode === 'login' ? 'Sign In' : 'Create Account'}
                </>
              )}
            </button>

            {mode === 'login' && (
              <div className="text-center -mt-1">
                <button
                  type="button"
                  onClick={() => {
                    window.alert(
                      'Password reset is coming soon.\n\n' +
                      'For now, email the admin at admin@fmaa.com with your registered email address and we\'ll reset it manually.'
                    );
                  }}
                  className="text-xs font-mono opacity-60 hover:opacity-100 underline"
                  data-testid="forgot-password-btn"
                >
                  Forgot password?
                </button>
              </div>
            )}
          </form>

          {/* Footer */}
          <p className="text-center mt-6 text-sm opacity-60">
            {mode === 'login' ? (
              <>Don't have an account? <button onClick={() => setMode('register')} className="underline">Register</button></>
            ) : (
              <>Already have an account? <button onClick={() => setMode('login')} className="underline">Sign in</button></>
            )}
          </p>
        </div>

        {/* What This App Does */}
        <div className="mt-8 space-y-4">
          <div className="text-center">
            <h2 className="font-display text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
              Train like an apprentice. Think like a CPA.
            </h2>
            <p className="font-mono text-xs opacity-70 leading-relaxed">
              FMAA pairs you with a Master Accountant powered by Claude 4.5.
              Every scenario is generated live from real-world financial
              statements — no static question banks, no memorization shortcuts.
            </p>
          </div>
          <div className="space-y-3 pt-2">
            <div className="flex gap-3" data-testid="feature-generate">
              <div className="w-1 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--primary)' }} />
              <div>
                <div className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>Dynamic Financial Statements</div>
                <div className="text-xs opacity-70">Income Statements, Balance Sheets, Cash Flows & T-Accounts generated on demand across 20 chapters.</div>
              </div>
            </div>
            <div className="flex gap-3" data-testid="feature-mastery">
              <div className="w-1 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--primary)' }} />
              <div>
                <div className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>Mastery Tracking & Coaching</div>
                <div className="text-xs opacity-70">The app measures every attempt and tells you exactly what to study next — no guesswork.</div>
              </div>
            </div>
            <div className="flex gap-3" data-testid="feature-tutor">
              <div className="w-1 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--primary)' }} />
              <div>
                <div className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>AI Tutor, Formulas & Calculator</div>
                <div className="text-xs opacity-70">Ask the Master Accountant anything, see step-by-step math, and keep a floating calculator on every page.</div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export function UserMenu({ user, onLogout }) {
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      onLogout();
    } catch (err) {
      console.error('Logout error:', err);
      onLogout(); // Logout anyway on frontend
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded hover:bg-[#F0EBE1] transition-all"
        data-testid="user-menu-btn"
      >
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: 'var(--primary)' }}>
          {user.name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
        </div>
        <span className="font-mono text-sm hidden md:block">{user.name || user.email}</span>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute right-0 top-full mt-2 w-48 rounded shadow-lg z-50"
            style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}
          >
            <div className="p-3 border-b" style={{ borderColor: 'var(--border)' }}>
              <div className="font-semibold text-sm">{user.name}</div>
              <div className="text-xs opacity-60">{user.email}</div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full text-left px-3 py-2 text-sm hover:bg-[#F0EBE1] transition-all text-[#8B0000]"
              data-testid="logout-btn"
            >
              Sign Out
            </button>
          </motion.div>
        </>
      )}
    </div>
  );
}
